import { type JSX, useEffect, useRef, useState } from "react"
import { type AgentRecord, createAgent, deleteAgent } from "./api"

// Label/value pairs from Agent::MODELS, in registry order (Default first).
export type ModelOption = [string, string]

// The workspace's agent roster. The roster itself lives in the app shell
// (workspace_app.tsx) because the rail's sublist shows it too — this pane
// just renders it. Click an agent to open it as a chat (renaming happens
// there). Live status (what an agent is doing right now) comes later from
// the event feed.
export function Agents({
  agents,
  failed,
  agentsUrl,
  modelOptions,
  onAgentCreated,
  onAgentDeleted,
  onOpenAgent,
}: {
  agents: AgentRecord[] | null
  failed: boolean
  agentsUrl: string
  modelOptions: ModelOption[]
  onAgentCreated: (agent: AgentRecord) => void
  onAgentDeleted: (agentId: string) => void
  onOpenAgent: (agentId: string) => void
}): JSX.Element {
  const [composing, setComposing] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  // Confirmed, because it stops a working agent and there is no undo in the
  // UI. The transcript survives on both sides, which is what the wording says.
  async function remove(agent: AgentRecord): Promise<void> {
    const label = agent.name ?? "this agent"
    if (!window.confirm(`Delete ${label}? It stops working and leaves the roster. Its transcript is kept.`)) return

    setDeleting(agent.id)
    setProblem(null)
    const ok = await deleteAgent(agentsUrl, agent.id)
    setDeleting(null)
    if (ok) onAgentDeleted(agent.id)
    else setProblem(`Could not delete ${label}. Try again.`)
  }

  return (
    <article className="workspace__doc">
      <header className="workspace__doc-head workspace__doc-head--split">
        <div>
          <h1 className="workspace__doc-title">Agents</h1>
          <p className="workspace__doc-meta">
            {agents ? `${agents.length} ${agents.length === 1 ? "agent" : "agents"} in this workspace.` : failed ? "Could not load agents." : "Loading…"}
          </p>
        </div>
        {agents && (
          <button type="button" className="btn workspace__agents-add" onClick={() => setComposing(true)}>
            New agent
          </button>
        )}
      </header>

      {agents && agents.length === 0 && (
        <p className="workspace__agents-empty">No agents yet. Add one to get started.</p>
      )}

      {agents && agents.length > 0 && (
        <ul className="workspace__agents">
          {agents.map((agent) => (
            <li key={agent.id} className="workspace__agent">
              <button
                type="button"
                className="workspace__agent-open"
                onClick={() => onOpenAgent(agent.id)}
              >
                <div className="workspace__agent-main">
                  <p className="workspace__agent-name">
                    {agent.name ?? "Unnamed agent"}
                    <span className="workspace__agent-model">{agent.model_label}</span>
                  </p>
                  <p className="workspace__agent-task">No activity yet</p>
                </div>
                <span className="workspace__agent-go" aria-hidden="true">→</span>
              </button>
              <button
                type="button"
                className="workspace__agent-delete"
                aria-label={`Delete ${agent.name ?? "agent"}`}
                title="Delete agent"
                disabled={deleting === agent.id}
                onClick={() => void remove(agent)}
              >
                {deleting === agent.id ? "…" : "×"}
              </button>
            </li>
          ))}
        </ul>
      )}

      {problem && <p className="workspace__agents-empty" role="alert">{problem}</p>}

      {composing && (
        <NewAgentModal
          agentsUrl={agentsUrl}
          modelOptions={modelOptions}
          onCreated={(agent) => {
            onAgentCreated(agent)
            setComposing(false)
          }}
          onClose={() => setComposing(false)}
        />
      )}
    </article>
  )
}

// Customize before creating: name and model. Mounted only while open, so its
// local state resets naturally each time.
function NewAgentModal({
  agentsUrl,
  modelOptions,
  onCreated,
  onClose,
}: {
  agentsUrl: string
  modelOptions: ModelOption[]
  onCreated: (agent: AgentRecord) => void
  onClose: () => void
}): JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [name, setName] = useState("")
  const [model, setModel] = useState(modelOptions[0]?.[1] ?? "default")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  async function submit(): Promise<void> {
    if (saving) return
    setSaving(true)
    setError(null)

    const fields: { name?: string; model?: string } = { model }
    const trimmed = name.trim()
    if (trimmed) fields.name = trimmed

    const agent = await createAgent(agentsUrl, fields)
    setSaving(false)
    if (agent) onCreated(agent)
    else setError("Could not create the agent. Try again.")
  }

  return (
    <dialog
      ref={dialogRef}
      className="modal"
      aria-labelledby="new-agent-title"
      onClose={onClose}
      onCancel={onClose}
    >
      <div className="modal__panel">
        <header className="modal__head">
          <h2 id="new-agent-title" className="modal__title">New agent</h2>
          <button type="button" className="modal__close" aria-label="Close" onClick={onClose}>&times;</button>
        </header>

        <form
          className="modal__form"
          onSubmit={(event) => {
            event.preventDefault()
            void submit()
          }}
        >
          {error && <div className="modal__errors">{error}</div>}

          <label className="field">
            <span className="field__label">Name</span>
            <input
              type="text"
              value={name}
              maxLength={120}
              placeholder="e.g. Prover"
              autoFocus
              onChange={(event) => setName(event.target.value)}
            />
          </label>

          <label className="field">
            <span className="field__label">Model</span>
            <select value={model} onChange={(event) => setModel(event.target.value)}>
              {modelOptions.map(([ label, value ]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>

          <div className="modal__actions">
            <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn--primary" disabled={saving}>
              {saving ? "Creating…" : "Create agent"}
            </button>
          </div>
        </form>
      </div>
    </dialog>
  )
}
